import { ArrowRight, Check, FileText } from "lucide-react";
import { motion } from "motion/react";
import { ExampleTag, shotProps } from "@/components/blocks/frames/Frames";
import { EASE, SPRING } from "@/components/motion/motion";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const NOTES = [
	{ key: "a", className: "top-[30%] left-[4%] -rotate-3" },
	{ key: "b", className: "top-[44%] right-[5%] rotate-2" },
	{ key: "c", className: "top-[68%] left-[18%] rotate-1" },
] as const;

const appear = (visible: boolean, delay = 0) => ({
	initial: false,
	animate: { opacity: visible ? 1 : 0, y: visible ? 0 : 10 },
	transition: { duration: 0.5, ease: EASE, delay: visible ? delay : 0 },
});

/**
 * One drawing that builds up with the collaboration steps:
 * 0 understand the process, 1 scope a first version (the wireframe draws),
 * 2 build it (the wireframe fills, demo approved), 3 hand over and evolve.
 */
export function BlueprintScene({
	stage,
	className,
}: {
	stage: number;
	className?: string;
}) {
	const { dict } = useI18n();
	const blueprint = dict.scenes.blueprint;
	const scoped = stage >= 1;
	const built = stage >= 2;
	const live = stage >= 3;

	return (
		<div
			{...shotProps}
			className={cn(
				"paper shot-shadow relative aspect-[4/3.4] w-full overflow-hidden rounded-3xl border select-none",
				className,
			)}
		>
			<div className="blueprint absolute inset-0 [mask-image:none]" />
			<ExampleTag className="absolute top-3 right-3 sm:top-4 sm:right-4">
				{dict.common.illustrationBadge}
			</ExampleTag>

			{/* The process as it runs today. */}
			<ol className="absolute top-[13%] left-[5%] flex w-[90%] items-center gap-1.5 sm:top-[7%] sm:w-[78%] sm:gap-[2%]">
				{(["a", "b", "c", "d"] as const).map((key, index) => {
					const priority = key === "b" && scoped;
					return (
						<li key={key} className="flex min-w-0 flex-1 items-center gap-[4%]">
							<span
								className={cn(
									"flex-1 truncate rounded-lg border border-dashed px-1 py-1.5 text-center text-[10px] font-medium transition-[background-color,border-color,color] duration-500 sm:px-2 sm:text-[11px]",
									priority
										? "border-solid border-signal bg-signal text-signal-foreground"
										: "border-foreground/30 bg-background",
								)}
							>
								{blueprint.flow[key]}
							</span>
							{index < 3 ? (
								<ArrowRight className="hidden size-3 shrink-0 text-foreground/40 sm:block" />
							) : null}
						</li>
					);
				})}
			</ol>

			{/* What people say about it. */}
			{NOTES.map((note, index) => (
				<motion.p
					initial={false}
					key={note.key}
					className={cn(
						"absolute max-w-[46%] rounded-md border bg-background px-3 py-2 text-[11.5px] leading-snug font-medium shadow-sm",
						note.className,
					)}
					animate={{
						opacity: scoped ? 0 : 1,
						scale: scoped ? 0.92 : 1,
					}}
					transition={{
						duration: 0.45,
						ease: EASE,
						delay: scoped ? 0 : 0.1 + index * 0.08,
					}}
				>
					{blueprint.notes[note.key]}
				</motion.p>
			))}

			{/* First version: drawn as a wireframe, then filled. */}
			<div className="absolute top-[24%] left-[12%] h-[56%] w-[70%]">
				<motion.div
					className="absolute inset-0"
					initial={false}
					animate={{
						clipPath: scoped ? "inset(0% 0% 0% 0%)" : "inset(0% 100% 0% 0%)",
					}}
					transition={{ duration: 1.1, ease: EASE }}
				>
					<Wireframe filled={built} />
				</motion.div>

				<motion.span
					className="absolute -top-3 left-4 rounded-full bg-foreground px-2.5 py-1 font-mono text-[9.5px] font-medium text-background"
					{...appear(scoped, 0.6)}
				>
					{blueprint.scope}
				</motion.span>

				<motion.span
					initial={false}
					className="absolute -top-3 right-4 flex items-center gap-1.5 rounded-full border border-signal bg-background px-2.5 py-1 text-[10px] font-semibold"
					animate={{ opacity: built ? 1 : 0, scale: built ? 1 : 0.6 }}
					transition={{ ...SPRING, delay: built ? 0.7 : 0 }}
				>
					<span className="flex size-3.5 items-center justify-center rounded-full bg-signal text-signal-foreground">
						<Check className="size-2.5" strokeWidth={3} />
					</span>
					{blueprint.demo}
				</motion.span>
			</div>

			{/* In service: documented, then improved release after release. */}
			<motion.div
				className="absolute bottom-[5%] left-[5%] flex items-center gap-3"
				{...appear(live, 0.2)}
			>
				<span className="flex items-center gap-2 rounded-full bg-foreground px-3 py-1.5 text-[10.5px] font-semibold text-background">
					<span className="relative flex size-2">
						<span className="pulse-ring absolute inset-0 rounded-full bg-signal" />
						<span className="relative size-2 rounded-full bg-signal" />
					</span>
					{blueprint.live}
				</span>
				<span className="hidden items-center gap-2 font-mono text-[10px] text-muted-foreground sm:flex">
					{blueprint.next}
					{["v1.1", "v1.2", "v1.3"].map((version, index) => (
						<motion.span
							initial={false}
							key={version}
							className="rounded border border-dashed border-foreground/30 px-1.5 py-0.5"
							animate={{ opacity: live ? 1 : 0 }}
							transition={{
								duration: 0.4,
								delay: live ? 0.6 + index * 0.2 : 0,
							}}
						>
							{version}
						</motion.span>
					))}
				</span>
			</motion.div>

			<motion.div
				className="absolute right-[5%] bottom-[5%] w-[26%]"
				{...appear(live, 0.4)}
			>
				<div className="relative">
					<span className="absolute inset-0 translate-x-2 -translate-y-2 rounded-lg border bg-muted" />
					<div className="relative rounded-lg border bg-background p-2.5">
						<p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold">
							<FileText className="size-3 text-signal-ink" />
							<span className="truncate">{blueprint.docs}</span>
						</p>
						{["w-full", "w-3/4", "w-5/6"].map((width) => (
							<span
								key={width}
								className={cn(
									"mb-1 block h-1 rounded-full bg-foreground/10",
									width,
								)}
							/>
						))}
					</div>
				</div>
			</motion.div>
		</div>
	);
}

/** The first version's screen: outline layer under a fill layer. */
function Wireframe({ filled }: { filled: boolean }) {
	const blocks = [
		{
			key: "bar",
			className: "inset-x-0 top-0 h-[13%] rounded-t-xl",
			fill: "bg-foreground",
		},
		{
			key: "side",
			className: "top-[13%] bottom-0 left-0 w-[22%] rounded-bl-xl",
			fill: "bg-muted",
		},
		{
			key: "row1",
			className: "top-[22%] left-[27%] right-[5%] h-[14%] rounded-lg",
			fill: "bg-signal/15",
		},
		{
			key: "row2",
			className: "top-[40%] left-[27%] right-[5%] h-[14%] rounded-lg",
			fill: "bg-muted",
		},
		{
			key: "row3",
			className: "top-[58%] left-[27%] right-[5%] h-[14%] rounded-lg",
			fill: "bg-muted",
		},
		{
			key: "cta",
			className: "bottom-[8%] right-[5%] h-[11%] w-[28%] rounded-lg",
			fill: "bg-signal",
		},
	];

	return (
		<div className="absolute inset-0 rounded-xl border-2 border-dashed border-foreground/35 bg-background/70">
			{blocks.map((block, index) => (
				<div key={block.key} className={cn("absolute", block.className)}>
					<div className="absolute inset-0 rounded-[inherit] border border-dashed border-foreground/30" />
					<motion.div
						initial={false}
						className={cn("absolute inset-0 rounded-[inherit]", block.fill)}
						animate={{ opacity: filled ? 1 : 0 }}
						transition={{
							duration: 0.5,
							ease: EASE,
							delay: filled ? 0.1 + index * 0.1 : 0,
						}}
					/>
				</div>
			))}
			<motion.div
				initial={false}
				className="absolute top-[26%] left-[31%] flex w-[40%] flex-col gap-1.5"
				animate={{ opacity: filled ? 1 : 0 }}
				transition={{ duration: 0.4, delay: filled ? 0.6 : 0 }}
			>
				<span className="h-1.5 w-full rounded-full bg-foreground/50" />
				<span className="h-1.5 w-2/3 rounded-full bg-foreground/25" />
			</motion.div>
		</div>
	);
}
